import { Link } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';
import { Upsell } from '../../components/plan/Upsell';
import { usePlan } from '../../components/plan/usePlan';
import { TicketHead } from '../../components/TicketHead';
import type { CommunityTemplateRead } from '../../types/api';
import { useBrandIdentity } from '../knowledge-base/useBrandIdentity';
import { useCollection } from '../knowledge-base/useCollection';
import { useSignedAssetUrl } from '../knowledge-base/useSignedAssetUrl';
import { useBusiness } from '../onboarding/useBusiness';
import {
  COMMUNITY_STATUS_BLURB,
  COMMUNITY_STATUS_LABEL,
  communityStatusTone,
  isCommunityTemplateEditable,
} from './communityStatus';
import { COMMUNITY_TEMPLATE_NEW_PATH, communityTemplatePath } from './communityTemplatePrefill';
import { communityTemplatesApi } from './communityTemplatesApi';
import { CommunityDesignPreview } from './CommunityDesignPreview';
import { brandPalette, type BrandPalette } from './communityZones';
import { useStartFromOwnTemplate } from './useStartFromOwnTemplate';

/** One row's thumbnail: the design drawn in CSS over its signed upload, if any. */
function RowPreview({ item, palette }: { item: CommunityTemplateRead; palette: BrandPalette }) {
  const { url } = useSignedAssetUrl(item.image_path);
  return (
    <CommunityDesignPreview
      className="design-preview--row"
      zones={item.zones}
      imageUrl={url}
      palette={palette}
      label={`${item.title} design`}
    />
  );
}

/**
 * The member's own community templates — the studio's entry point, and the one
 * place a draft that was never submitted can be found again. Every row can be
 * used in a brief straight away: review decides sharing, not use.
 *
 * Every status is on this list, including the ones the author cannot change:
 * knowing a template is with the reviewers is the reason to come here. Deletes
 * go through the app's undo window like every other collection, so a mistaken
 * one costs six seconds rather than an afternoon's work.
 */
function MyCommunityTemplates({ businessId }: { businessId: string }) {
  const collection = useCollection(communityTemplatesApi, businessId);
  const { identity } = useBrandIdentity(businessId);
  const palette = brandPalette(identity?.colors);
  const error = collection.error ?? collection.deleteError;
  const { start, startingId } = useStartFromOwnTemplate();
  // Making, editing and posting on a template outside the gallery is Private
  // templates. Without it the list stays: view, delete, and use an approved one.
  const { can } = usePlan();
  const canPrivate = can('private_templates');

  return (
    <div className="kb-section">
      <div className="kb-section__header">
        <div>
          <h2 className="kb-section__title">Your templates</h2>
          <p className="kb-section__lede">
            Every template here is yours to post on now, whatever its status. Submitting for review
            is only for sharing one with every member: once approved it joins the community gallery.
          </p>
        </div>
        {canPrivate ? (
          <Link className="button button--primary" to={COMMUNITY_TEMPLATE_NEW_PATH}>
            Create a template
          </Link>
        ) : null}
      </div>

      {canPrivate ? null : <Upsell feature="private_templates" />}

      {error ? (
        <p className="form__error" role="alert">
          {error}
        </p>
      ) : null}

      {collection.isLoading ? (
        <p className="kb-section__status" role="status">
          Loading your templates
        </p>
      ) : collection.items.length === 0 ? (
        <div className="kb-section__empty">
          <p className="kb-section__empty-text">
            You have not made one yet. Build a design from colours, shapes and text boxes, or upload
            one you already post on, and you can create a post with it straight away.
          </p>
          {canPrivate ? (
            <Link className="button button--primary" to={COMMUNITY_TEMPLATE_NEW_PATH}>
              Create a template
            </Link>
          ) : null}
        </div>
      ) : (
        <ul className="community-list">
          {collection.items.map((item) => {
            const editable = canPrivate && isCommunityTemplateEditable(item.status);
            const canUse = canPrivate || item.status === 'approved';
            return (
              <li className="community-row" key={item.id}>
                <TicketHead label="Community template" timestamp={item.created_at} />
                <div className="community-row__body">
                  <RowPreview item={item} palette={palette} />
                  <div className="community-row__main">
                    <span className="community-row__title">{item.title}</span>
                    <span className="community-row__description">
                      {item.description || COMMUNITY_STATUS_BLURB[item.status]}
                    </span>
                    {item.status === 'rejected' && item.review_note ? (
                      <span className="community-row__note">{item.review_note}</span>
                    ) : null}
                    <span className="community-row__meta">
                      <span className={communityStatusTone(item.status)}>
                        {COMMUNITY_STATUS_LABEL[item.status]}
                      </span>
                      {item.status === 'approved' ? (
                        <span className="community-card__uses">
                          {item.use_count === 1 ? 'Used once' : `Used ${item.use_count} times`}
                        </span>
                      ) : null}
                    </span>
                  </div>
                  <div className="community-row__actions">
                    <button
                      className="button button--primary"
                      type="button"
                      disabled={startingId !== null || !canUse}
                      onClick={() => void start(item)}
                    >
                      {startingId === item.id ? 'Opening' : 'Use'}
                    </button>
                    <Link className="button button--ghost" to={communityTemplatePath(item.id)}>
                      {editable ? 'Edit' : 'View'}
                    </Link>
                    <button
                      className="button button--ghost button--danger-text"
                      type="button"
                      onClick={() =>
                        collection.removeWithUndo({
                          id: item.id,
                          label: item.title,
                          failureMessage: 'That template could not be deleted.',
                        })
                      }
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** The member's own community templates at `/templates/community`. */
export function MyCommunityTemplatesPage() {
  const { businessId, isLoading, error } = useBusiness();

  return (
    <main className="page">
      <PageHeader
        section="Templates / Community"
        title="My community templates"
        lede="Designs you made. Use one right away; submit it for review only to share it with every member."
      />

      {isLoading ? (
        <p className="kb-section__status" role="status">
          Loading your business
        </p>
      ) : !businessId ? (
        <div className="kb-section">
          <p className="form__notice">
            {error ??
              'No business is linked to this account yet. Finish setup and the studio opens.'}
          </p>
          <Link className="button" to="/onboarding">
            Finish setup
          </Link>
        </div>
      ) : (
        <MyCommunityTemplates businessId={businessId} />
      )}

      <p className="pack-gallery__foot">
        Looking for designs other members shared?{' '}
        <Link to="/templates?tab=community">Browse the community gallery</Link>.
      </p>
    </main>
  );
}
